import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Calendar, Clock, User, MapPin, CreditCard, Shield, 
  ChevronLeft, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookingPage() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, api, isPatient } = useAuth();
  
  const [doctor, setDoctor] = useState(location.state?.doctor || null);
  const [schedule, setSchedule] = useState(location.state?.schedule || null);
  const [slot, setSlot] = useState(location.state?.slot || null);
  const [healthPlans, setHealthPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const [paymentType, setPaymentType] = useState('private');
  const [selectedHealthPlan, setSelectedHealthPlan] = useState('');
  const [healthPlanCardNumber, setHealthPlanCardNumber] = useState('');
  const [healthPlanHolderName, setHealthPlanHolderName] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!doctor) {
      fetchDoctorData();
    }
    fetchHealthPlans();
  }, [doctorId]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/agendar/${doctorId}`, { state: location.state });
    }
  }, [isAuthenticated]);

  const fetchDoctorData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/doctors/${doctorId}`);
      setDoctor(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do médico');
    }
  };

  const fetchHealthPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/health-plans`);
      setHealthPlans(response.data);
    } catch (error) {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const bookingData = {
        doctor_id: doctorId,
        schedule_id: schedule.id,
        date: schedule.date,
        time_slot: slot.start_time,
        payment_type: paymentType,
        health_plan_id: paymentType === 'health_plan' ? selectedHealthPlan : null,
        health_plan_card_number: paymentType === 'health_plan' ? healthPlanCardNumber : null,
        health_plan_holder_name: paymentType === 'health_plan' ? healthPlanHolderName : null,
        notes: notes || null
      };

      await api.post('/patient/appointments', bookingData);
      setBookingSuccess(true);
      toast.success('Consulta agendada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao agendar consulta');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="booking-success">
        <Navbar />
        <div className="container-app py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">Consulta Agendada!</h1>
            <p className="text-slate-600 mb-8">
              Sua consulta com {doctor?.full_name} foi confirmada para {schedule?.date && format(new Date(schedule.date), "dd 'de' MMMM", { locale: ptBR })} às {slot?.start_time}.
            </p>
            <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                  <User className="w-7 h-7 text-slate-400" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900">{doctor?.full_name}</p>
                  <p className="text-sm text-slate-600">{doctor?.specialty_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <Calendar className="w-4 h-4" />
                <span>{schedule?.date && format(new Date(schedule.date), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span>{slot?.start_time}</span>
              </div>
            </div>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/paciente/consultas')} className="bg-primary" data-testid="view-appointments-btn">
                Ver Minhas Consultas
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                Voltar ao Início
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!doctor || !schedule || !slot) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-16 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Dados incompletos</h1>
          <p className="text-slate-600 mb-6">Selecione um horário na página do médico</p>
          <Button onClick={() => navigate(`/medico/${doctorId}`)}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar para o perfil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-page">
      <Navbar />

      <div className="container-app py-8">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar
          </button>

          <h1 className="text-2xl font-bold text-slate-900 mb-6">Confirmar Agendamento</h1>

          {/* Appointment Summary */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
            <h2 className="font-semibold text-slate-900 mb-4">Resumo da Consulta</h2>
            <div className="flex items-center gap-4 mb-4 pb-4 border-b border-slate-100">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                {doctor.photo ? (
                  <img src={doctor.photo} alt={doctor.full_name} className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <User className="w-7 h-7 text-slate-400" />
                )}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{doctor.full_name}</p>
                <p className="text-sm text-slate-600">{doctor.specialty_name}</p>
                <p className="text-xs text-slate-500">CRM {doctor.crm}/{doctor.crm_state}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4 text-primary" />
                <span>{format(new Date(schedule.date), "dd/MM/yyyy", { locale: ptBR })}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4 text-primary" />
                <span>{slot.start_time}</span>
              </div>
              {doctor.provider_name && (
                <div className="col-span-2 flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>{doctor.provider_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Booking Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Forma de Pagamento</h2>

            <RadioGroup value={paymentType} onValueChange={setPaymentType} className="space-y-3 mb-6">
              <div className="flex items-center space-x-3 p-4 border border-slate-200 rounded-xl hover:border-primary transition-colors cursor-pointer">
                <RadioGroupItem value="private" id="private" data-testid="payment-private" />
                <Label htmlFor="private" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    <span className="font-medium">Consulta Particular</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Pagamento na clínica</p>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-4 border border-slate-200 rounded-xl hover:border-primary transition-colors cursor-pointer">
                <RadioGroupItem value="health_plan" id="health_plan" data-testid="payment-health-plan" />
                <Label htmlFor="health_plan" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="font-medium">Convênio / Plano de Saúde</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Atendimento pelo convênio</p>
                </Label>
              </div>
            </RadioGroup>

            {/* Mostrar preço apenas quando selecionar Particular */}
            {paymentType === 'private' && doctor.private_price && (
              <div className="p-4 bg-blue-50 rounded-xl mb-6 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Valor da Consulta</p>
                    <p className="text-sm text-slate-600">Pagamento realizado na clínica</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">
                    R$ {doctor.private_price.toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {paymentType === 'health_plan' && (
              <div className="space-y-4 mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="space-y-2">
                  <Label htmlFor="health-plan">Convênio</Label>
                  <Select value={selectedHealthPlan} onValueChange={setSelectedHealthPlan}>
                    <SelectTrigger data-testid="select-health-plan">
                      <SelectValue placeholder="Selecione seu convênio" />
                    </SelectTrigger>
                    <SelectContent>
                      {healthPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="card-number">Número da Carteira</Label>
                  <Input
                    id="card-number"
                    placeholder="Número da carteira do convênio"
                    value={healthPlanCardNumber}
                    onChange={(e) => setHealthPlanCardNumber(e.target.value)}
                    data-testid="card-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="holder-name">Nome do Titular</Label>
                  <Input
                    id="holder-name"
                    placeholder="Nome como consta na carteira"
                    value={healthPlanHolderName}
                    onChange={(e) => setHealthPlanHolderName(e.target.value)}
                    data-testid="holder-name"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2 mb-6">
              <Label htmlFor="notes">Observações (opcional)</Label>
              <Textarea
                id="notes"
                placeholder="Informe o motivo da consulta ou outras observações"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="booking-notes"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90"
              disabled={loading || (paymentType === 'health_plan' && !selectedHealthPlan)}
              data-testid="confirm-booking-btn"
            >
              {loading ? 'Agendando...' : 'Confirmar Agendamento'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
